import { useTranslation } from "@sagot/i18n";
import { Badge, EmptyState } from "@sagot/ui";
import type { VersionDiff } from "../p4-types";
import { CompareColumn } from "./compare-columns";

const V = "engage.workflows.versions";

/** Whether the two sides hold the same steps, settings, translations and notes. */
export function isIdentical(data: VersionDiff): boolean {
  return data.added + data.removed + data.changed + data.moved === 0 && !data.settings_changed && !data.translations_changed && !data.notes_changed;
}

interface DiffBodyProps {
  data: VersionDiff;
  baseTitle: string;
  targetTitle: string;
  /** Said instead of the two columns when nothing differs (a comparison with the default says "matches the default"). */
  identical?: { title: string; body: string };
}

/** What a comparison found: the counts, then the two sides with each step marked added, removed, changed or moved. */
export function DiffBody({ data, baseTitle, targetTitle, identical }: DiffBodyProps) {
  const { t } = useTranslation();
  return (
    <>
      <ul className="flex flex-wrap gap-2" aria-label={t(`${V}.summary`)} data-testid="diff-summary">
        <li><Badge tone="success">{t(`${V}.counts.added`, { count: data.added })}</Badge></li>
        <li><Badge tone="danger">{t(`${V}.counts.removed`, { count: data.removed })}</Badge></li>
        <li><Badge tone="warning">{t(`${V}.counts.changed`, { count: data.changed })}</Badge></li>
        <li><Badge tone="brand">{t(`${V}.counts.moved`, { count: data.moved })}</Badge></li>
        {data.settings_changed ? <li><Badge tone="warning">{t(`${V}.settingsChanged`)}</Badge></li> : null}
        {data.translations_changed ? <li><Badge tone="warning">{t(`${V}.translationsChanged`)}</Badge></li> : null}
        {data.notes_changed ? <li><Badge tone="warning">{t("engage.workflows.extras.notesChanged")}</Badge></li> : null}
      </ul>
      {isIdentical(data) ? (
        <EmptyState icon="check-circle" headingLevel={3} title={identical?.title ?? t(`${V}.identicalTitle`)} description={identical?.body ?? t(`${V}.identicalBody`)} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <CompareColumn title={baseTitle} side="base" paths={data.paths} />
          <CompareColumn title={targetTitle} side="target" paths={data.paths} />
        </div>
      )}
    </>
  );
}
